import { RingFinishEnum } from '@jouzen/ecom-components';
import { useLocale } from 'next-intl';

import useCurrentRoute from '@/app/[locale]/product/_components/HardwarePDP/_hooks/useCurrentRoute';
import {
  type HeroCarouselSlideProps,
  HeroCarouselSlideVariant,
} from '@/app/[locale]/product/_components/HardwarePDP/HeroCarousel/HeroCarouselSlide';

const MOBILE_MEDIA = '(max-width: 767px)';
const TABLET_MEDIA = '(min-width: 768px)';

const RING_ASSORTMENT_SLIDE: HeroCarouselSlideProps = {
  contents: {
    variant: HeroCarouselSlideVariant.FullBleed,
    alt: 'wizard_tech_specs_image_alt',
    srcSet: [
      {
        media: MOBILE_MEDIA,
        src: 'hardware-pdp/hero-carousel/or4-all-finishes-mobile.png?ar=1:1&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.5&fp-z=1',
      },
      {
        media: TABLET_MEDIA,
        src: 'hardware-pdp/hero-carousel/or4-all-finishes.png?ar=6:5&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.61&fp-z=1.28',
      },
    ],
  },
  headerInverse: true,
};

const SLIDES_BY_FINISH: Partial<
  Record<RingFinishEnum, HeroCarouselSlideProps[]>
> = {
  [RingFinishEnum.Silver]: [
    {
      contents: {
        variant: HeroCarouselSlideVariant.FullBleed,
        alt: 'ring_pdp_2025_silver_primary_image_alt',
        srcSet: [
          {
            media: MOBILE_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-silver-lifestyle.jpg?ar=1:1&fit=crop&crop=focalpoint&fp-x=0&fp-y=0.67&fp-z=1.35',
          },
          {
            media: TABLET_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-silver-lifestyle.jpg?ar=6:5&fit=crop&crop=focalpoint&fp-x=0&fp-y=0.64&fp-z=1.45',
          },
        ],
      },
      headerInverse: true,
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingFront,
        alt: 'pdp_ring_color_silver_label',
        src: 'hardware-pdp/hero-carousel/or4-silver-front-view.png',
      },
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingAngle,
        alt: 'pdp_ring_color_silver_label',
        bgColor: '#CDCDCD',
        src: 'hardware-pdp/hero-carousel/or4-silver-angle-view.png',
      },
    },
  ],
  [RingFinishEnum.Black]: [
    {
      contents: {
        variant: HeroCarouselSlideVariant.FullBleed,
        alt: 'ring_pdp_2025_black_primary_image_alt',
        srcSet: [
          {
            media: MOBILE_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-black-lifestyle.jpg?ar=1:1&fit=crop&crop=focalpoint&fp-x=0.46&fp-y=0.5&fp-z=1',
          },
          {
            media: TABLET_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-black-lifestyle.jpg?ar=6:5&fit=crop&crop=focalpoint&fp-x=0.52&fp-y=0.57&fp-z=1.27',
          },
        ],
      },
      headerInverse: true,
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingFront,
        alt: 'pdp_ring_color_black_title',
        src: 'hardware-pdp/hero-carousel/or4-black-front-view.png',
      },
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingAngle,
        alt: 'pdp_ring_color_black_title',
        bgColor: '#878787',
        src: 'hardware-pdp/hero-carousel/or4-black-angle-view.png',
      },
      headerInverse: true,
    },
  ],
  [RingFinishEnum.Stealth]: [
    {
      contents: {
        variant: HeroCarouselSlideVariant.FullBleed,
        alt: 'ring_pdp_2025_stealth_primary_image_alt',
        srcSet: [
          {
            media: MOBILE_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-stealth-lifestyle.jpg?ar=1:1&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.5&fp-z=1',
          },
          {
            media: TABLET_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-stealth-lifestyle.jpg?ar=6:5&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.5&fp-z=1',
          },
        ],
      },
      headerInverse: true,
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingFront,
        alt: 'pdp_ring_color_stealth_title',
        src: 'hardware-pdp/hero-carousel/or4-stealth-front-view.png',
      },
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingAngle,
        alt: 'pdp_ring_color_stealth_title',
        bgColor: '#595959',
        src: 'hardware-pdp/hero-carousel/or4-stealth-angle-view.png',
      },
      headerInverse: true,
    },
  ],
  [RingFinishEnum.BrushedSilver]: [
    {
      contents: {
        variant: HeroCarouselSlideVariant.FullBleed,
        alt: 'ring_pdp_2025_brushed_silver_primary_image_alt',
        srcSet: [
          {
            media: MOBILE_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-brushed-silver-lifestyle.jpg?ar=1:1&fit=crop&crop=focalpoint&fp-x=0.46&fp-y=0.5&fp-z=1.15',
          },
          {
            media: TABLET_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-brushed-silver-lifestyle.jpg?ar=6:5&fit=crop&crop=focalpoint&fp-x=0.53&fp-y=0.47&fp-z=1.2',
          },
        ],
      },
      headerInverse: true,
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingFront,
        alt: 'pdp_ring_color_brushed_silver_title',
        src: 'hardware-pdp/hero-carousel/or4-brushed-silver-front-view.png',
      },
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingAngle,
        alt: 'pdp_ring_color_brushed_silver_title',
        bgColor: '#C2C2C2',
        src: 'hardware-pdp/hero-carousel/or4-brushed-silver-angle-view.png',
      },
    },
  ],
  [RingFinishEnum.Gold]: [
    {
      contents: {
        variant: HeroCarouselSlideVariant.FullBleed,
        alt: 'ring_pdp_2025_gold_primary_image_alt',
        srcSet: [
          {
            media: MOBILE_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-gold-lifestyle.jpg?ar=1:1&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.6&fp-z=1.4',
          },
          {
            media: TABLET_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-gold-lifestyle.jpg?ar=6:5&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.55&fp-z=1.5',
          },
        ],
      },
      headerInverse: true,
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingFront,
        alt: 'pdp_ring_color_gold_title',
        src: 'hardware-pdp/hero-carousel/or4-gold-front-view.png',
      },
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingAngle,
        alt: 'pdp_ring_color_gold_title',
        bgColor: '#EFE9D9',
        src: 'hardware-pdp/hero-carousel/or4-gold-angle-view.png',
      },
    },
  ],
  [RingFinishEnum.RoseGold]: [
    {
      contents: {
        variant: HeroCarouselSlideVariant.FullBleed,
        alt: 'ring_pdp_2025_rose_gold_primary_image_alt',
        srcSet: [
          {
            media: MOBILE_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-rose-gold-lifestyle.jpg?ar=1:1&fit=crop&crop=focalpoint&fp-x=0.48&fp-y=0.5&fp-z=1.1',
          },
          {
            media: TABLET_MEDIA,
            src: 'hardware-pdp/hero-carousel/or4-rose-gold-lifestyle.jpg?ar=6:5&fit=crop&crop=focalpoint&fp-x=0.52&fp-y=0.35&fp-z=1',
          },
        ],
      },
      headerInverse: true,
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingFront,
        alt: 'pdp_ring_color_rose_gold_title',
        src: 'hardware-pdp/hero-carousel/or4-rose-gold-front-view.png',
      },
    },
    {
      contents: {
        variant: HeroCarouselSlideVariant.RingAngle,
        alt: 'pdp_ring_color_rose_gold_title',
        bgColor: '#F0DACC',
        src: 'hardware-pdp/hero-carousel/or4-rose-gold-angle-view.png',
      },
    },
  ],
};

const useHeroCarouselSlides = (): HeroCarouselSlideProps[] => {
  const locale = useLocale();
  const { finish } = useCurrentRoute({ locale });
  return [
    ...(SLIDES_BY_FINISH[finish.value as RingFinishEnum] ?? []),
    RING_ASSORTMENT_SLIDE,
  ];
};

export default useHeroCarouselSlides;
